import {
  OWNER_WEB_DEFAULT_PAGE_SIZE,
  OWNER_WEB_MAX_PAGE_SIZE,
} from '@argink/shared/owner-web';
import {
  parseDashboardSnapshot,
  parseEnvelopeDetail,
  parseEnvelopeList,
  parseSessionInfo,
  parseSignedDocumentValidation,
  parseVerificationReport,
  toCreateEnvelopePayloadDto,
  toResendRequestDto,
  toStartEnvelopeRequestDto,
} from './adapters';
import { apiJson, apiRequest } from './client';
import { idempotencyKeyFor, stableFingerprint } from './idempotency';
import type { CreateEnvelopePayload, otpRequestSchema, otpVerifySchema } from './schemas';
import { ContractViolationError } from './types';
import type {
  DashboardSnapshot,
  DocumentFormat,
  EnvelopeDetail,
  EnvelopeListResponse,
  EnvelopeStatus,
  SessionInfo,
  SignedDocumentValidation,
  VerificationReport,
} from './types';
import type { z } from 'zod';

/**
 * Superfície de rede do owner web. **Toda** resposta passa pelo adapter
 * (`./adapters.ts`), que valida e traduz — nenhuma rota devolve o DTO cru e
 * nenhuma página vê snake_case. Toda requisição com corpo também é montada lá.
 */

// ---------------------------------------------------------------- auth (F02)

export async function requestOtp(
  input: z.infer<typeof otpRequestSchema>,
): Promise<{ status: 'accepted'; verificationHandle: string }> {
  const body = await apiJson<{ status: 'accepted'; verification_handle: string }>(
    '/web/auth/otp/request',
    { method: 'POST', json: input },
  );
  return { status: body.status, verificationHandle: body.verification_handle };
}

export async function verifyOtp(
  input: z.infer<typeof otpVerifySchema>,
): Promise<{ status: 'authenticated' }> {
  return apiJson<{ status: 'authenticated' }>('/web/auth/otp/verify', {
    method: 'POST',
    json: input,
  });
}

export async function getSession(): Promise<SessionInfo> {
  return parseSessionInfo(await apiJson<unknown>('/web/session'));
}

export async function logout(): Promise<void> {
  await apiRequest('/web/auth/logout', { method: 'POST' });
}

// ---------------------------------------------------------------- envelopes (F04, AMVP-08)

export interface ListEnvelopesParams {
  status?: EnvelopeStatus;
  /**
   * Busca GLOBAL (R-UX02-P02/L3A-2b) — o BFF varre todos os envios do tenant, não só
   * a página carregada. Quem chama já normaliza (trim; string só de espaço vira
   * `undefined`, nunca um `q=` vazio) — este ponto não repete a normalização.
   */
  q?: string;
  cursor?: string | null;
  limit?: number;
}

/** Valores CANÔNICOS do contrato compartilhado — reexportados para as telas não
 * reescreverem "20"/"100" à mão e derivarem do servidor com o tempo. */
export const DEFAULT_PAGE_SIZE = OWNER_WEB_DEFAULT_PAGE_SIZE;
export const MAX_PAGE_SIZE = OWNER_WEB_MAX_PAGE_SIZE;

export async function listEnvelopes(
  params: ListEnvelopesParams = {},
): Promise<EnvelopeListResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.q) search.set('q', params.q);
  if (params.cursor) search.set('cursor', params.cursor);
  // Pedir acima do teto é 400 no backend — clampar aqui evita erro que o usuário
  // não causou e não teria como entender.
  const limit = Math.min(params.limit ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);
  search.set('limit', String(limit));
  const qs = search.toString();
  return parseEnvelopeList(await apiJson<unknown>(`/web/envelopes${qs ? `?${qs}` : ''}`));
}

export async function getEnvelope(id: string): Promise<EnvelopeDetail> {
  return parseEnvelopeDetail(await apiJson<unknown>(`/web/envelopes/${encodeURIComponent(id)}`));
}

// ---------------------------------------------------------------- dashboard (R-UX02-P01/L3B)

/**
 * `GET /web/dashboard` — **exatamente uma chamada**, sem parâmetro (o tenant é
 * opaco, vindo da sessão). Substitui por completo o caminho antigo que derivava
 * a Home de `listEnvelopes({ limit: MAX_PAGE_SIZE })` — este endpoint nunca é
 * chamado pelo Dashboard, e o Dashboard nunca chama `listEnvelopes`.
 */
export async function getDashboard(): Promise<DashboardSnapshot> {
  return parseDashboardSnapshot(await apiJson<unknown>('/web/dashboard'));
}

export async function createEnvelope(input: {
  document: File;
  payload: CreateEnvelopePayload;
  /**
   * Chave da **série de disparo**, gerada e guardada por quem dispara
   * (`EnvelopeNewPage`) e repetida em todo retry da mesma série.
   *
   * Não é derivada do payload de propósito: o prazo nasce de "agora + preset"
   * (mandato §7), então uma chave por fingerprint mudaria a cada tentativa e o
   * retry de um `create` cuja RESPOSTA se perdeu criaria um SEGUNDO rascunho —
   * o servidor já tinha gravado o primeiro, o cliente é que nunca soube o id.
   *
   * Chave estável é metade: o backend também guarda um `requestHash` do pedido
   * e responde **409 `idempotency_conflict`** para a mesma chave com payload
   * diferente. Por isso quem dispara congela o PAYLOAD da série junto com a
   * chave — só o par estável produz replay.
   *
   * Formato: o header exige `[\x21-\x7e]{8,200}` (`apps/api/src/routes/web-envelopes.ts`);
   * `newIdempotencyKey()` (UUID) cabe. O protocolo do header não mudou.
   */
  idempotencyKey: string;
}): Promise<EnvelopeDetail> {
  // O JSON que vai no multipart é o DTO do backend — nunca o form da UI.
  const payloadDto = toCreateEnvelopePayloadDto(input.payload);
  const formData = new FormData();
  formData.append('document', input.document);
  formData.append('payload', JSON.stringify(payloadDto));
  return parseEnvelopeDetail(
    await apiJson<unknown>('/web/envelopes', {
      method: 'POST',
      formData,
      headers: { 'idempotency-key': input.idempotencyKey },
    }),
  );
}

export async function startEnvelope(input: {
  id: string;
  expectedUpdatedAt: string;
}): Promise<EnvelopeDetail> {
  const body = toStartEnvelopeRequestDto({ expectedUpdatedAt: input.expectedUpdatedAt });
  const idempotencyKey = idempotencyKeyFor(
    `start-envelope:${input.id}`,
    stableFingerprint(body),
  );
  return parseEnvelopeDetail(
    await apiJson<unknown>(`/web/envelopes/${encodeURIComponent(input.id)}/start`, {
      method: 'POST',
      json: body,
      headers: { 'idempotency-key': idempotencyKey },
    }),
  );
}

export async function cancelEnvelope(id: string): Promise<EnvelopeDetail> {
  // Corpo vazio `.strict()` no backend — nada a enviar.
  return parseEnvelopeDetail(
    await apiJson<unknown>(`/web/envelopes/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  );
}

/**
 * Reenvio POR SIGNATÁRIO. O corpo é camelCase por decisão do contrato congelado
 * (mesma forma de `/start`, que também é camelCase enquanto as *views* são
 * snake_case) — mas passa pelo adapter como todo o resto, para não existir "às
 * vezes converte, às vezes não".
 *
 * Sucesso é o próprio 2xx: o corpo da resposta não carrega informação que a UI
 * use, então não há shape para validar aqui.
 */
export async function resendNotification(input: {
  envelopeId: string;
  participantId: string;
}): Promise<{ status: 'queued' }> {
  const body = toResendRequestDto({ participantId: input.participantId });
  const idempotencyKey = idempotencyKeyFor(
    `resend:${input.envelopeId}:${input.participantId}`,
    stableFingerprint({ at: Math.floor(Date.now() / (60 * 1000)) }), // 1 tentativa nova por minuto
  );
  await apiRequest(
    `/web/envelopes/${encodeURIComponent(input.envelopeId)}/notifications/resend`,
    {
      method: 'POST',
      json: body,
      headers: { 'idempotency-key': idempotencyKey },
    },
  );
  return { status: 'queued' };
}

export function signedDocumentUrl(envelopeId: string, documentId: string): string {
  return `/web/envelopes/${encodeURIComponent(envelopeId)}/documents/${encodeURIComponent(documentId)}/signed`;
}

/** D-063/#100 — valida o artefato assinado ARMAZENADO (motor interno, sem quota,
 * sem upload). Só envelope `completed`; ver a explicação de diferença para o
 * Evidence Delivery na copy (`VALIDATE_EXPLANATION`). */
export async function validateSignedDocument(
  envelopeId: string,
): Promise<SignedDocumentValidation> {
  const raw = await apiJson<unknown>(
    `/web/envelopes/${encodeURIComponent(envelopeId)}/validate`,
    { method: 'POST', json: {} },
  );
  return parseSignedDocumentValidation(raw);
}

/** O relatório é remontado e reverificado a cada chamada — por isso o instante
 * carimbado é o da RESPOSTA, não um campo do backend (que não existe). */
export async function getEvidenceReport(envelopeId: string): Promise<VerificationReport> {
  const raw = await apiJson<unknown>(
    `/web/envelopes/${encodeURIComponent(envelopeId)}/evidence`,
  );
  return parseVerificationReport(raw, new Date().toISOString());
}

export function evidenceManifestUrl(envelopeId: string): string {
  return `/web/envelopes/${encodeURIComponent(envelopeId)}/evidence/manifest`;
}

export function evidenceBundleUrl(envelopeId: string): string {
  return `/web/envelopes/${encodeURIComponent(envelopeId)}/evidence/bundle`;
}

/**
 * Extensão do arquivo assinado por formato do documento (R-UX02-P02/L3B-B) —
 * espelha `apps/api/src/domain/owner-envelope/service.ts` (`cades ? 'p7s' :
 * 'pdf'`) do lado do fallback local; `xades` ainda não tem caminho de emissão
 * real (`domain/evidence-manifest.ts`), mas ganha uma extensão honesta mesmo
 * assim, para o fallback nunca cair num `undefined`.
 */
export const DOCUMENT_FORMAT_EXTENSION: Record<DocumentFormat, string> = {
  pades: 'pdf',
  cades: 'p7s',
  xades: 'xml',
};

/**
 * Extrai o nome de arquivo de um header `Content-Disposition` de download.
 * Prioridade para a forma estendida do RFC 5987/6266 (`filename*=UTF-8''...`,
 * percent-encoded — `decodeURIComponent`); sem ela, cai no `filename="..."`/
 * `filename=...` simples. `null` quando o header está ausente, ou presente mas
 * sem nome nenhum — nunca um nome inventado a partir daqui (quem chama decide o
 * fallback).
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded) return decoded;
    } catch {
      // percent-encoding malformado: cai para as formas simples abaixo, nunca lança.
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted && quoted[1] && quoted[1].length > 0) return quoted[1];

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  const bareTrimmed = bare?.[1]?.trim();
  if (bareTrimmed) return bareTrimmed;

  return null;
}

/** Remove um sufixo `.xxx` reconhecível (1 a 5 caracteres alfanuméricos) do
 * fim do nome — só para o fallback local não duplicar extensão
 * (`Contrato.pdf` + `.pdf` novo não pode virar `Contrato.pdf-signed.pdf`). */
function stripKnownExtension(name: string): string {
  return name.replace(/\.[a-z0-9]{1,5}$/i, '');
}

/**
 * Fallback local de nome do arquivo assinado — só usado quando o servidor NÃO
 * mandou `Content-Disposition` com nome nenhum (o caminho comum é o header do
 * servidor vencer, ver `downloadFile`). `<nome ou id>-signed.<ext>`, sem
 * duplicar extensão e sem NUNCA produzir `null`/`undefined` no nome — o defeito
 * que esta função substitui.
 */
export function signedDocumentFallbackName(nameOrId: string, format: DocumentFormat): string {
  const ext = DOCUMENT_FORMAT_EXTENSION[format];
  const base = stripKnownExtension(nameOrId.trim()) || nameOrId.trim() || 'documento';
  return `${base}-signed.${ext}`;
}

/** Header que os TRÊS endpoints de Evidence Delivery mandam (D-110) — o mesmo `manifestHash` que
 *  o corpo do relatório de verificação já expõe (`VerificationReport.manifestHash`, exibido pelo
 *  `VerdictCard`). Comparado byte a byte (após normalizar casing/whitespace) contra o hash que a
 *  tela já mostrou, ANTES de disponibilizar o Blob — nunca depois. */
const MANIFEST_HASH_HEADER = 'x-argink-manifest-sha256';

function normalizeManifestHash(value: string): string {
  return value.trim().toLowerCase();
}

/** Confere o header do download contra o `H` já exibido na tela (D-110, invariante 19). Ausência
 * ou divergência é FATO, não corrida perdida — `ContractViolationError` é a MESMA classe que já
 * cobre "resposta fora do contrato" em todo o resto do cliente (`lib/api/adapters.ts`), então
 * `describeApiError` já sabe traduzi-la sem copy nova. Nunca clica o anchor nem abre a janela
 * quando isto lança. `expectedManifestHash` ausente/nulo (chamador sem `H` para comparar, ou
 * chamada legada como o download do artefato assinado em `EnvelopeResultPage.tsx`) pula a
 * checagem — comportamento anterior preservado.
 */
function assertManifestHashMatches(response: Response, expectedManifestHash: string | null | undefined): void {
  if (!expectedManifestHash) return;
  const headerValue = response.headers.get(MANIFEST_HASH_HEADER);
  if (!headerValue || normalizeManifestHash(headerValue) !== normalizeManifestHash(expectedManifestHash)) {
    throw new ContractViolationError(
      MANIFEST_HASH_HEADER,
      headerValue,
      'header do download ausente ou diferente do hash do manifesto já exibido — arquivo não entregue',
    );
  }
}

/** Baixa um GET binário via `fetch` (mesma origem, cookie incluso) e dispara o
 * download do navegador via link sintético — funciona igual com o mock instalado
 * em `window.fetch` ou com a rede real, sem duas implementações (IMVP-11/26).
 *
 * O nome do arquivo baixado prefere o que o SERVIDOR mandou
 * (`Content-Disposition`, via `parseContentDispositionFilename`); só cai no
 * `fallbackName` de quem chama quando o header está ausente ou não traz nome
 * nenhum — nunca `null`/`undefined` no nome do arquivo salvo.
 *
 * `expectedManifestHash` (D-110): quando informado, o header `X-Argink-Manifest-SHA256` da
 * resposta precisa bater com ele ANTES do Blob ser criado — ver `assertManifestHashMatches`.
 * Omitido (chamadas que não são de Evidence Delivery, como o artefato assinado) preserva o
 * comportamento anterior, sem checagem nenhuma.
 */
export async function downloadFile(
  url: string,
  fallbackName: string,
  expectedManifestHash?: string | null,
): Promise<void> {
  const response = await apiRequest(url, { method: 'GET' });
  assertManifestHashMatches(response, expectedManifestHash);
  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('content-disposition')) ?? fallbackName;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/** "Ver manifesto": mesma busca via `fetch` (funciona com mock/rede real), mas
 * abre numa aba nova em vez de baixar — verbo "ver", não "baixar". Nunca usa
 * `window.open(url)` direto: isso navegaria fora da camada `fetch` interceptada
 * pelos mocks e quebraria em dev sem backend real.
 *
 * `expectedManifestHash` (D-110): mesma checagem de `downloadFile` — ver `assertManifestHashMatches`.
 */
export async function openFileInNewTab(url: string, expectedManifestHash?: string | null): Promise<void> {
  const response = await apiRequest(url, { method: 'GET' });
  assertManifestHashMatches(response, expectedManifestHash);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, '_blank', 'noopener');
  // Não revoga imediatamente — a aba nova precisa do object URL vivo enquanto
  // estiver aberta; o navegador libera ao fechar/recarregar o contexto.
}
